import { DeviceType } from "@/components/common/device-list/device-list";
import { isVirtualMediaDevice } from "@/utils/utils";
import { MediaDeviceErrors } from "@/locales/localeid";
import { fmtMsg } from "vue-glcommonui";

export interface MediaDeviceError {
  isError: boolean;
  localeKey: string;
  code: string;
}

export const createDefaultMediaError = (): MediaDeviceError => ({ isError: false, localeKey: "", code: "" });

export const getMediaDeviceErrorMessage = (error: MediaDeviceError): string => {
  return error.localeKey ? fmtMsg(error.localeKey) : "";
};

export enum CameraCustomErrorCode {
  CAMERA_DEVICE_IN_USE = "CAMERA_DEVICE_IN_USE",
  CAMERA_PERMISSION_DENIED = "CAMERA_PERMISSION_DENIED",
  CAMERA_NOT_FOUND = "CAMERA_NOT_FOUND",
}

export enum MicrophoneCustomErrorCode {
  MICROPHONE_DEVICE_IN_USE = "MICROPHONE_DEVICE_IN_USE",
  MICROPHONE_PERMISSION_DENIED = "MICROPHONE_PERMISSION_DENIED",
  MICROPHONE_ENABLE_NOISE_CANCELLING_FAILED = "MICROPHONE_ENABLE_NOISE_CANCELLING_FAILED",
  MICROPHONE_NOT_FOUND = "MICROPHONE_NOT_FOUND",
}

//#region Microphone Error Handling
export const mappingAgoraMicrophoneError = (error: any): MediaDeviceError => {
  if (!error || typeof error !== "object") {
    return {
      isError: true,
      localeKey: MediaDeviceErrors.MicrophoneInUse,
      code: MicrophoneCustomErrorCode.MICROPHONE_DEVICE_IN_USE,
    };
  }

  const lowerMessage = error.message ? error.message.toLowerCase() : "";

  if (lowerMessage.includes("device in use")) {
    return {
      isError: true,
      localeKey: MediaDeviceErrors.MicrophoneInUse,
      code: MicrophoneCustomErrorCode.MICROPHONE_DEVICE_IN_USE,
    };
  }

  if (lowerMessage.includes("permission denied")) {
    return {
      isError: true,
      localeKey: MediaDeviceErrors.MicrophonePermissionDenied,
      code: MicrophoneCustomErrorCode.MICROPHONE_PERMISSION_DENIED,
    };
  }

  if (lowerMessage.includes("aidenoiserprocessor")) {
    return {
      isError: true,
      localeKey: MediaDeviceErrors.MicrophoneEnableNoiseCancellingFailed,
      code: MicrophoneCustomErrorCode.MICROPHONE_ENABLE_NOISE_CANCELLING_FAILED,
    };
  }

  if (lowerMessage.includes("microphone_not_found")) {
    return {
      isError: true,
      localeKey: MediaDeviceErrors.MicrophoneNotFound,
      code: MicrophoneCustomErrorCode.MICROPHONE_NOT_FOUND,
    };
  }

  return { 
    isError: true, 
    code: MicrophoneCustomErrorCode.MICROPHONE_DEVICE_IN_USE, 
    localeKey: MediaDeviceErrors.MicrophoneInUse 
  };
};
//#endregion

//#region Camera Error Handling
export const mappingAgoraCameraError = (error: any): MediaDeviceError => {
  if (!error || typeof error !== "object") {
    return {
      isError: true,
      localeKey: MediaDeviceErrors.CameraInUse,
      code: CameraCustomErrorCode.CAMERA_DEVICE_IN_USE,
    };
  }

  const lowerMessage = error.message ? error.message.toLowerCase() : "";

  if (lowerMessage.includes("device in use")) {
    return {
      isError: true,
      localeKey: MediaDeviceErrors.CameraInUse,
      code: CameraCustomErrorCode.CAMERA_DEVICE_IN_USE,
    };
  }

  if (lowerMessage.includes("camera_not_found")) {
    return {
      isError: true,
      localeKey: MediaDeviceErrors.CameraNotFound,
      code: CameraCustomErrorCode.CAMERA_NOT_FOUND,
    };
  }

  if (lowerMessage.includes("permission denied")) {
    return {
      isError: true,
      localeKey: MediaDeviceErrors.CameraPermissionDenied,
      code: CameraCustomErrorCode.CAMERA_PERMISSION_DENIED,
    };
  }

  return { 
    isError: true, 
    code: CameraCustomErrorCode.CAMERA_DEVICE_IN_USE, 
    localeKey: MediaDeviceErrors.CameraInUse 
  };
};
//#endregion

export function filterAndSortMicrophones(microphones: MediaDeviceInfo[]): DeviceType[] {
  return microphones
    .filter((microphone) => !isVirtualMediaDevice(microphone.label))
    .map((microphone) => ({
      deviceId: microphone.deviceId,
      label: microphone.label,
      kind: microphone.kind,
      groupId: microphone.groupId || "",
      isDefault: microphone.deviceId === "default",
    }));
}

export function filterAndSortSpeakers(speakers: MediaDeviceInfo[]): DeviceType[] {
  return speakers
    .filter((speaker) => !isVirtualMediaDevice(speaker.label))
    .map((speaker) => ({
      deviceId: speaker.deviceId,
      label: speaker.label,
      kind: speaker.kind,
      groupId: speaker.groupId || "",
      isDefault: speaker.deviceId === "default",
    }));
}
